import React, { useState, useEffect } from 'react';
import './Terms.css';

export const Terms = () => {
  const [activeSection, setActiveSection] = useState('');

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      window.history.pushState(null, '', `#${id}`);
      setActiveSection(id);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -60% 0px' }
    );

    const sections = document.querySelectorAll('section[id]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-header">
        <h1>Terms and Conditions</h1>
        <p>Qinto Marketplace Platform</p>
        <p className="effective-date">Effective Date: November 23, 2025 | Last Updated: November 23, 2025</p>
      </div>

      <div className="legal-container">
        <aside className="legal-sidebar">
          <h3>Table of Contents</h3>
          <ul>
            <li><a href="#terms-1" onClick={(e) => scrollToSection(e, 'terms-1')} className={activeSection === 'terms-1' ? 'active' : ''}>Introduction and Acceptance</a></li>
            <li><a href="#terms-2" onClick={(e) => scrollToSection(e, 'terms-2')} className={activeSection === 'terms-2' ? 'active' : ''}>User Accounts</a></li>
            <li><a href="#terms-3" onClick={(e) => scrollToSection(e, 'terms-3')} className={activeSection === 'terms-3' ? 'active' : ''}>User Conduct and Restrictions</a></li>
            <li><a href="#terms-4" onClick={(e) => scrollToSection(e, 'terms-4')} className={activeSection === 'terms-4' ? 'active' : ''}>Listings and Transactions</a></li>
            <li><a href="#terms-5" onClick={(e) => scrollToSection(e, 'terms-5')} className={activeSection === 'terms-5' ? 'active' : ''}>Fees and Charges</a></li>
            <li><a href="#terms-6" onClick={(e) => scrollToSection(e, 'terms-6')} className={activeSection === 'terms-6' ? 'active' : ''}>Intellectual Property Rights</a></li>
            <li><a href="#terms-7" onClick={(e) => scrollToSection(e, 'terms-7')} className={activeSection === 'terms-7' ? 'active' : ''}>Privacy and Data Protection</a></li>
            <li><a href="#terms-8" onClick={(e) => scrollToSection(e, 'terms-8')} className={activeSection === 'terms-8' ? 'active' : ''}>Safety and Trust</a></li>
            <li><a href="#terms-9" onClick={(e) => scrollToSection(e, 'terms-9')} className={activeSection === 'terms-9' ? 'active' : ''}>Disclaimers and Limitations of Liability</a></li>
            <li><a href="#terms-10" onClick={(e) => scrollToSection(e, 'terms-10')} className={activeSection === 'terms-10' ? 'active' : ''}>Indemnification</a></li>
            <li><a href="#terms-11" onClick={(e) => scrollToSection(e, 'terms-11')} className={activeSection === 'terms-11' ? 'active' : ''}>Dispute Resolution and Governing Law</a></li>
            <li><a href="#terms-12" onClick={(e) => scrollToSection(e, 'terms-12')} className={activeSection === 'terms-12' ? 'active' : ''}>Modifications to Terms</a></li>
            <li><a href="#terms-13" onClick={(e) => scrollToSection(e, 'terms-13')} className={activeSection === 'terms-13' ? 'active' : ''}>Termination</a></li>
            <li><a href="#terms-14" onClick={(e) => scrollToSection(e, 'terms-14')} className={activeSection === 'terms-14' ? 'active' : ''}>General Provisions</a></li>
            <li><a href="#terms-15" onClick={(e) => scrollToSection(e, 'terms-15')} className={activeSection === 'terms-15' ? 'active' : ''}>Contact Information</a></li>
          </ul>
        </aside>

        <div className="legal-content">


          <section id="terms-1">
            <h2>1. Introduction and Acceptance</h2>
            <p>Welcome to Qinto. These Terms and Conditions ("Terms") govern your access to and use of the Qinto mobile application and website at qinto.in (collectively, the "Platform"), operated by Qinto ("we," "us," or "our").</p>
            <p>By accessing or using the Platform, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use the Platform.</p>

            <h3>1.1 Eligibility</h3>
            <p>You must be at least 18 years of age to use the Platform. By using the Platform, you represent and warrant that you are of legal age to form a binding contract under Indian law and meet all eligibility requirements. If you are under 18 years of age, you may use the Platform only under the supervision and prior consent of a parent or legal guardian.</p>

            <h3>1.2 Platform Overview</h3>
            <p>Qinto is a peer-to-peer marketplace platform that enables users to buy and sell pre-owned items locally. We facilitate connections between buyers and sellers but are not a party to any transactions between users.</p>
          </section>

          <section id="terms-2">
            <h2>2. User Accounts</h2>

            <h3>2.1 Account Creation</h3>
            <p>To access certain features of the Platform, you must create an account by providing accurate and complete information, including your phone number or email address. You agree to:</p>
            <ul>
              <li>Provide true, accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security and confidentiality of your login credentials</li>
              <li>Notify us immediately of any unauthorized access or security breach</li>
            </ul>

            <h3>2.2 Account Responsibilities</h3>
            <p>You are solely responsible for all activities that occur under your account. We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent, abusive, or illegal activity.</p>

            <h3>2.3 Account Deletion</h3>
            <p>You may delete your account at any time through Profile → Settings → Account → Delete Account. Upon deletion, your listings and messages will be removed in accordance with our Privacy Policy.</p>
          </section>

          <section id="terms-3">
            <h2>3. User Conduct and Restrictions</h2>

            <h3>3.1 Acceptable Use</h3>
            <p>You agree to use the Platform only for lawful purposes and in accordance with these Terms. You agree NOT to:</p>
            <ul>
              <li>Violate any applicable local, state, national, or international law</li>
              <li>Post false, inaccurate, misleading, defamatory, or libelous content</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Engage in fraudulent activities or scams</li>
              <li>Harass, abuse, threaten, or intimidate other users</li>
              <li>Upload or transmit viruses, malware, or any harmful code</li>
              <li>Attempt to gain unauthorized access to the Platform or other users' accounts</li>
              <li>Scrape, data mine, or use automated tools to access the Platform</li>
              <li>Sell prohibited items including weapons, drugs, illegal goods, or stolen property</li>
              <li>Interfere with or disrupt the Platform's operation</li>
            </ul>

            <h3>3.2 Prohibited Items</h3>
            <p>The following items may not be listed on Qinto:</p>
            <ul>
              <li>Illegal substances, drugs, or narcotics</li>
              <li>Weapons, firearms, ammunition, or explosives</li>
              <li>Stolen goods or counterfeit items</li>
              <li>Products infringing intellectual property rights</li>
              <li>Live animals (pets may be allowed with restrictions)</li>
              <li>Adult or explicit content</li>
              <li>Items that violate any applicable laws or regulations</li>
            </ul>
            <p>We reserve the right to remove any listing that violates these restrictions without notice.</p>
          </section>

          <section id="terms-4">
            <h2>4. Listings and Transactions</h2>

            <h3>4.1 Creating Listings</h3>
            <p>Sellers may create listings for pre-owned items by providing:</p>
            <ul>
              <li>Clear and accurate photos</li>
              <li>Truthful title and description</li>
              <li>Appropriate category and tags</li>
              <li>Fair price in Indian Rupees (INR)</li>
              <li>Accurate location information</li>
            </ul>
            <p>Sellers are responsible for ensuring all listing information is accurate, complete, and not misleading.</p>

            <h3>4.2 Listing Management</h3>
            <p>Sellers may edit or delete their listings at any time. We reserve the right to remove, modify, or reject any listing that violates these Terms or applicable laws.</p>

            <h3>4.3 Buyer-Seller Communication</h3>
            <p>Buyers and sellers communicate through the in-app chat feature. All communication should be respectful, honest, and related to the transaction. We may monitor communications to ensure compliance with these Terms and for safety purposes.</p>

            <h3>4.4 Transactions and Payments</h3>
            <div className="warning-box">
              <p><strong>Important:</strong> Qinto is a marketplace platform that facilitates connections between buyers and sellers. We are not a party to any transaction between users.</p>
            </div>
            <ul>
              <li>Buyers and sellers negotiate prices and payment methods directly</li>
              <li>Currently, Qinto does not process payments. Users agree on payment methods independently (cash, UPI, bank transfer, etc.)</li>
              <li>In the future, we may introduce secure in-app payment options</li>
              <li>Users are responsible for ensuring safe and secure payment practices</li>
            </ul>

            <h3>4.5 No Guarantee of Transactions</h3>
            <p>We do not guarantee that:</p>
            <ul>
              <li>Listings are accurate or available</li>
              <li>Transactions will be completed</li>
              <li>Buyers will pay or sellers will deliver</li>
              <li>Items are as described</li>
            </ul>
            <p>Users engage in transactions at their own risk.</p>
          </section>

          <section id="terms-5">
            <h2>5. Fees and Charges</h2>

            <h3>5.1 Current Fee Structure</h3>
            <p>Creating an account, browsing items, and listing products on Qinto is currently <strong>free</strong>. We reserve the right to introduce fees, premium features, or paid services in the future.</p>

            <h3>5.2 Future Fee Changes</h3>
            <p>If we introduce fees, we will provide at least 30 days' notice through the Platform or via email. Continued use of the Platform after fee changes take effect constitutes acceptance of the new fees.</p>
          </section>

          <section id="terms-6">
            <h2>6. Intellectual Property Rights</h2>

            <h3>6.1 Platform Content</h3>
            <p>All content on the Platform, including text, graphics, logos, icons, images, software, and other materials (excluding user-generated content), is the property of Qinto or its licensors and is protected by Indian and international copyright, trademark, and other intellectual property laws.</p>

            <h3>6.2 User-Generated Content</h3>
            <p>By posting content on the Platform (listings, photos, messages, reviews), you grant Qinto a non-exclusive, worldwide, royalty-free, perpetual, and transferable license to use, reproduce, modify, adapt, publish, display, and distribute such content for the purpose of operating and promoting the Platform.</p>
            <p>You represent and warrant that:</p>
            <ul>
              <li>You own or have the necessary rights to the content you post</li>
              <li>Your content does not infringe any third-party intellectual property rights</li>
              <li>Your content complies with these Terms and applicable laws</li>
            </ul>

            <h3>6.3 Copyright Infringement</h3>
            <p>If you believe that content on the Platform infringes your copyright, please contact us at support@qinto.in with:</p>
            <ul>
              <li>Description of the copyrighted work</li>
              <li>Location of the infringing content</li>
              <li>Your contact information</li>
              <li>A statement of good faith belief that the use is not authorized</li>
            </ul>
          </section>

          <section id="terms-7">
            <h2>7. Privacy and Data Protection</h2>
            <p>Your privacy is important to us. Our collection, use, and disclosure of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Platform, you consent to our Privacy Policy.</p>
          </section>

          <section id="terms-8">
            <h2>8. Safety and Trust</h2>

            <h3>8.1 User Safety</h3>
            <p>Your safety is our priority. We recommend:</p>
            <ul>
              <li>Meeting in public places for in-person exchanges</li>
              <li>Bringing a friend or family member</li>
              <li>Inspecting items before payment</li>
              <li>Using secure payment methods</li>
              <li>Trusting your instincts and reporting suspicious behavior</li>
            </ul>

            <h3>8.2 Reporting</h3>
            <p>You can report suspicious users, listings, or inappropriate behavior through:</p>
            <ul>
              <li>The "Report" button on listings or user profiles</li>
              <li>The in-app chat "Report" option</li>
              <li>Contacting support at support@qinto.in</li>
            </ul>
            <p>We review all reports and take appropriate action, which may include warning users, removing content, or terminating accounts.</p>

            <h3>8.3 Verification</h3>
            <p>We may implement user verification features in the future, including phone verification, email verification, or identity verification. Verification does not guarantee trustworthiness and users should still exercise caution.</p>
          </section>

          <section id="terms-9">
            <h2>9. Disclaimers and Limitations of Liability</h2>

            <h3>9.1 Platform Provided "AS IS"</h3>
            <div className="warning-box">
              <p>THE PLATFORM IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.</p>
            </div>

            <h3>9.2 No Liability for User Conduct</h3>
            <p>Qinto is not responsible for:</p>
            <ul>
              <li>The conduct of users on or off the Platform</li>
              <li>The accuracy, quality, safety, or legality of listings</li>
              <li>The ability of sellers to deliver items</li>
              <li>The ability of buyers to pay</li>
              <li>Disputes between users</li>
              <li>Loss, damage, or theft occurring during transactions</li>
            </ul>

            <h3>9.3 Limitation of Liability</h3>
            <div className="warning-box">
              <p>TO THE MAXIMUM EXTENT PERMITTED BY INDIAN LAW, QINTO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE PLATFORM, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.</p>
              <p>OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM OR RELATED TO THE PLATFORM SHALL NOT EXCEED INR 1,000 OR THE AMOUNT YOU PAID TO US IN THE PAST 12 MONTHS, WHICHEVER IS GREATER.</p>
            </div>

            <h3>9.4 Third-Party Services</h3>
            <p>The Platform may contain links to third-party websites, services, or advertisements. We are not responsible for the content, privacy practices, or conduct of third parties. Your use of third-party services is at your own risk.</p>
          </section>

          <section id="terms-10">
            <h2>10. Indemnification</h2>
            <p>You agree to indemnify, defend, and hold harmless Qinto, its officers, directors, employees, and agents from and against any and all claims, liabilities, damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising from:</p>
            <ul>
              <li>Your use of the Platform</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your listings or content posted on the Platform</li>
              <li>Your conduct or transactions with other users</li>
            </ul>
          </section>

          <section id="terms-11">
            <h2>11. Dispute Resolution and Governing Law</h2>

            <h3>11.1 Governing Law</h3>
            <p>These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles.</p>

            <h3>11.2 Jurisdiction</h3>
            <p>Any disputes arising out of or relating to these Terms or the Platform shall be subject to the exclusive jurisdiction of the courts in Hyderabad, Telangana, India.</p>

            <h3>11.3 Informal Resolution</h3>
            <p>Before initiating any legal action, you agree to attempt to resolve disputes informally by contacting us at support@qinto.in. We will attempt to resolve the dispute through good-faith negotiations within 30 days.</p>

            <h3>11.4 User Disputes</h3>
            <p>Disputes between users (buyers and sellers) are the responsibility of the parties involved. While we may provide assistance or mediation, we are not obligated to resolve user-to-user disputes and are not liable for the outcome.</p>
          </section>

          <section id="terms-12">
            <h2>12. Modifications to Terms</h2>
            <p>We may modify these Terms at any time by posting the updated Terms on the Platform and updating the "Last Updated" date. Material changes will be notified through:</p>
            <ul>
              <li>In-app notifications</li>
              <li>Email notifications to your registered email</li>
              <li>Prominent notice on the Platform</li>
            </ul>
            <p>Your continued use of the Platform after changes take effect constitutes acceptance of the modified Terms. If you do not agree to the modified Terms, you must stop using the Platform.</p>
          </section>

          <section id="terms-13">
            <h2>13. Termination</h2>

            <h3>13.1 Termination by You</h3>
            <p>You may terminate your account at any time through the account settings or by contacting us at support@qinto.in.</p>

            <h3>13.2 Termination by Us</h3>
            <p>We reserve the right to suspend or terminate your account or access to the Platform at any time, with or without notice, for any reason, including:</p>
            <ul>
              <li>Violation of these Terms</li>
              <li>Fraudulent, abusive, or illegal activity</li>
              <li>Requests by law enforcement or government agencies</li>
              <li>Extended periods of inactivity</li>
              <li>Technical or security reasons</li>
            </ul>

            <h3>13.3 Effect of Termination</h3>
            <p>Upon termination:</p>
            <ul>
              <li>Your right to use the Platform will immediately cease</li>
              <li>Your listings will be removed</li>
              <li>We may delete your account data in accordance with our Privacy Policy</li>
              <li>Sections of these Terms that by their nature should survive termination will remain in effect</li>
            </ul>
          </section>

          <section id="terms-14">
            <h2>14. General Provisions</h2>

            <h3>14.1 Entire Agreement</h3>
            <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and Qinto regarding the Platform and supersede all prior agreements.</p>

            <h3>14.2 Severability</h3>
            <p>If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will remain in full force and effect.</p>

            <h3>14.3 Waiver</h3>
            <p>Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision.</p>

            <h3>14.4 Assignment</h3>
            <p>You may not assign or transfer these Terms or your account without our prior written consent. We may assign these Terms without restriction.</p>

            <h3>14.5 Force Majeure</h3>
            <p>We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including acts of God, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, accidents, pandemics, strikes, or shortages of transportation, facilities, fuel, energy, labor, or materials.</p>

            <h3>14.6 Language</h3>
            <p>These Terms are drafted in English. In the event of any conflict between the English version and any translation, the English version shall prevail.</p>
          </section>

          <section id="terms-15">
            <h2>15. Contact Information</h2>
            <p>If you have any questions, concerns, or feedback regarding these Terms, please contact us:</p>
            <div className="info-box">
              <p><strong>Qinto Support</strong><br />
              Email: support@qinto.in<br />
              Website: <a href="https://qinto.in" target="_blank" rel="noopener noreferrer">https://qinto.in</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;